{
/*
  # Example usage and documentation:

  # a button that shows/hides the entire `nav[ada-menu]`
  <button 
    ada-menu--nav-toggler 
    aria-controls="main-menu"
    aria-label="Toggle Main Menu"
  >...</button>

  <nav 
    # an [ada-menu] property and an HTML ID tells this plugin to use this element
    ada-menu
    id="main-menu"

    # when the <button[ada-menu--nav-toggle]> is pressed on mobile the `nav[ada-menu]` will open and fill the screen
    # if you don't want the nav to fill the entire screen you can account for that here 
    # for example, if you want the main header to still be visible when the menu is open
    ada-menu--subtract-from-menu-height-when-opened=".main-header"

    # on mobile the menu behaves slightly differently:
    # - sub menus don't autoclose (that would be too jumpy)
    # - the <nav> fills the entire height if opened
    # - etc
    # so you can set the breakpoint for mobile behavior here
    ada-menu--mobile-breakpoint="768"

    # this determines how far down the togglers should propagate
    # for example, at some point you may just want to have the menu items always show/not convert <a> links of sub sub menus
    ada-menu--depth="999" 

    # also good to have aria label
    aria-label="Main Navigation"
  >

    # The menu structure should be something like this so [tab] works in a logical order
    # The only requirements is to have an `a[ada-menu--toggler][aria-controls="some-id"] and an [id] attributes on the appropriate elements.
    <ul>
      <li>
        <a href="#" ada-menu--toggler aria-controls="menu-1">Item With Submenu</a>
        <ul id="menu-1">
          <li><a href="...">...</a></li>
          <li><a href="...">...</a></li>
          <li><a href="...">...</a></li>  
        </ul>
      </li>
      <li>
        <a href="#">Item Without Submenu</a>
      </li>  
    </ul>
  </nav>
*/

  class adaMenu {
    constructor(el){
      this.mouseDown = false
      this.el = el
      this._convertTogglerControlsToButtons()
      document.addEventListener('mousedown', () => this._onMouseDown())
      document.addEventListener('touchdown', () => this._onMouseDown())
      document.addEventListener('mouseup', () => this._onMouseUp())
      document.addEventListener('touchup', () => this._onMouseUp())
      document.addEventListener('focus', e => this._onFocusChange(e), true)
      document.addEventListener('keydown', e => this._onKeydown(e), true)
      document.addEventListener('click', e => {
        if (!this.mouseDown && (this.el.contains(e.target) || e.target.closest(`[ada-menu--nav-toggler][aria-controls="${this.el.id}"]`) || !this.el.contains(e.target))) {
          this._onClick(e)
        }
      })
    }

    _onKeydown(event){
      if(event.key === "ArrowUp" || event.key === "ArrowLeft"){
        if(document.activeElement.closest('[ada-menu]')){
          event.preventDefault()
          this._simulateTabKeyPress(-1, true)     
        }
      } 
      else if(event.key === "ArrowRight"){
        if(document.activeElement.closest('[ada-menu]')){
          event.preventDefault()
          this._simulateTabKeyPress(1, true)
        }
      } else if(event.key === "ArrowDown"){
        if(document.activeElement.closest('[ada-menu]')){
          event.preventDefault()
          const isOpen = document.activeElement.hasAttribute('ada-menu--toggler') && document.activeElement.getAttribute('aria-expanded') == 'true'
          if(!isOpen){
            this._handleClickInsideMenu(event)
            this._simulateTabKeyPress(1, false)
          } else {
            this._simulateTabKeyPress(1, false)
          }
        }
      } else if(event.key === "Home") {
        const focusableElements = Array.from(this.el.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'));
        if(focusableElements.length){
          event.preventDefault()
          focusableElements[0].focus()
        }
      } else if(event.key === "End") {
        const focusableElements = Array.from(this.el.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'));
        if(focusableElements.length){
          event.preventDefault()
          focusableElements[focusableElements.length - 1].focus()
        }
      }
    }

    _simulateTabKeyPress(val, onlyVisibleItems) {
      const focusableElements = Array.from(document.querySelectorAll('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'));
      
      // Filter out elements that are not visible if onlyVisibleItems is true
      const filteredElements = onlyVisibleItems
        ? focusableElements.filter(el => el.offsetWidth > 0 && el.offsetHeight > 0 && getComputedStyle(el).visibility !== 'hidden')
        : focusableElements;

      const currentIndex = filteredElements.indexOf(document.activeElement);
      
      // Calculate the next index, ensuring it wraps around
      const nextIndex = (currentIndex + val + filteredElements.length) % filteredElements.length;

      // Focus the next element, only if there is a next element
      if (filteredElements.length > 0) {
        filteredElements[nextIndex].focus();
      }
  }

    // change <a> controllers to <button>s 
    _convertTogglerControlsToButtons(){
      const maxDepth = this.el.getAttribute('ada-menu--depth') || 99999999

      this.el.querySelectorAll('[ada-menu--toggler]').forEach(toggler => {
        let depth = 1
        let parent = toggler.closest('ul') // Start from the closest parent <ul>
        if(parent && parent.id){
          let controllingToggler = this.el.querySelector(`[aria-controls="${parent.id}"]`)
          while(parent && controllingToggler) { // Check if the parent <ul> has an id
            depth += 1
            parent = parent.parentNode.closest('ul') // Move up to the next parent <ul>
          }

          // Skip if depth exceeds maxDepth
          if (depth > parseInt(maxDepth)){
            toggler.removeAttribute('aria-controls')
            toggler.removeAttribute('ada-menu--toggler')
            return
          }
        }
        
        toggler.setAttribute('aria-expanded', false)

        if(toggler.tagName == "A" && toggler.href[toggler.href.length - 1] != "#"){
          console.error("A top level menu toggler cannot also be a link: ", toggler.textContent)
        }
        const button = document.createElement('button')
        Array.from(toggler.attributes).forEach(attr => button.setAttribute(attr.name, attr.value))
        button.innerHTML = toggler.innerHTML
        
        const indicator = document.createElement('div')
        indicator.setAttribute('ada-menu--toggle-indicator', 1)
        button.append(indicator)

        toggler.replaceWith(button)
      })
    }

    _onMouseDown(){
      this.mouseDown = true
    }

    _onMouseUp(){
      this.mouseDown = false
    }

    _onClick(e){
      // we have to do this because Safari does not set focus on click for some reason...
      e.target.focus()

      if(this.el.contains(document.activeElement)){
        this._handleClickInsideMenu(e)
      } else {
        const mainToggler = e.target.closest(`[ada-menu--nav-toggler][aria-controls="${this.el.id}"]`)
        if(mainToggler){
          this._closeMenusWithoutFocus()
          if(this.el.offsetWidth == 0){
            mainToggler.setAttribute('aria-expanded', true)
            this.el.style.display = 'block'
            this.el.querySelector('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])').focus()
            this._disableScroll()
            document.querySelector('html').classList.add('ada-menu-open')
            mainToggler.dispatchEvent(new CustomEvent('main_toggler_opened'))
          } else {
            mainToggler.setAttribute('aria-expanded', false)
            this.el.style.display = 'none'
            this._enableScroll()
            document.querySelector('html').classList.remove('ada-menu-open')
            mainToggler.dispatchEvent(new CustomEvent('main_toggler_closed'))
          }
        } else {
          this._handleInteractionOutideMenu(e)
        }
      }
    }

    //enables scrolling again
    _enableScroll(){
      if(!this._isScreenMobile()){
        return
      }
      const scrollY = document.body.style.top
      document.body.style.top = ''
      document.body.style.position = ''
      document.body.style.width = ''
      window.scrollTo(0, parseInt(scrollY || '0') * -1)
    }

    //disables scroll (when menu is open)
    _disableScroll(){
      if(!this._isScreenMobile()){
        return
      }
      document.body.style.top = 0
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      const attr = 'ada-menu--subtract-from-menu-height-when-opened'
      if(this.el.getAttribute(attr)){
        const elsToSub = document.querySelectorAll(this.el.getAttribute(attr))
        if(elsToSub.length){
          let total = 0
          elsToSub.forEach(el => total += el.offsetHeight)
          if(total > 0){
            this.el.style.height = `calc(100vh - ${total}px)`
          }
        }
      }
    }

    _onFocusChange(e){
      if(this.mouseDown){
        return // we don't care about focus changes when the mouse is down - that means click will handle it
      }
      if(this.el.contains(document.activeElement)){
        this._handleFocusChangeInsideMenu()
      } else {
        this._handleInteractionOutideMenu(e)
      }
      this.lastFocusedElement = document.activeElement
    }

    // Check if submenu is off-screen and apply "push-left" class
    // we never remove these because then it ALWAYS returns true 
    // when it is display: none which causes bugs
    _addPushClasses(el){
      const contentRect = el.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      if (contentRect.left + contentRect.width > viewportWidth) {
        el.classList.add('push-left')
      } else {
        // el.classList.remove('push-left')
      }
      if (contentRect.left < 0) {
        el.classList.add('push-right')
      } else {
        // el.classList.remove('push-right')
      }
    }

    _handleClickInsideMenu(e){
      const toggler = e.target.closest('[aria-controls]')
      if(toggler){
        const content = this._getControlledContent(toggler)
        // if we click on a toggle content button
        if(content){
          if(content.style.display == 'flex'){
            content.style.display = 'none'
            toggler.setAttribute('aria-expanded', false)
          } else {
            content.style.display = 'flex'
            toggler.setAttribute('aria-expanded', true)
            this._addPushClasses(content)
          }
        }
        toggler.closest(`[ada-menu]`).dispatchEvent(new CustomEvent('content_toggler_clicked', { detail: { toggler } }))
      }

      // this snippet closes the main (mobile) toggler if the link contains a `#`
      // because it might be on the same page
      const link = e.target.closest('a')
      if(link?.href && link.href.indexOf('#') != -1){

        if(link.href[0] == '#' && !document.querySelector(link.hash)) {
          window.location.href = `/${link.hash}`;
          return
        }

        const mainToggler = this._getMainToggler()
        if(mainToggler && mainToggler.offsetWidth > 0){
          if(this._isScreenMobile()){
            mainToggler.setAttribute('aria-expanded', false)
            this.el.style.display = 'none'
            this._enableScroll()
          }
        }
      }

      // this should only happen on desktop
      // because menus opening/closing on mobile makes things jump around
      if(!this._isScreenMobile()){
        this._closeMenusWithoutFocus()
      }
    }

    _isScreenMobile(){
      const attr = 'ada-menu--mobile-breakpoint'
      const breakPoint = this.el.hasAttribute(attr) ? parseInt(this.el.getAttribute(attr)) : 768
      return window.innerWidth <= breakPoint
    }

    // close any menus that don't contain this element
    _handleFocusChangeInsideMenu(){
      this._closeMenusWithoutFocus()
    }

    // close all menus and menu toggler
    _handleInteractionOutideMenu(e){
      if(!this.el.contains(e.target) && !e?.target?.closest(`[ada-menu--nav-toggler][aria-controls="${this.el.id}"]`)) {
        this._closeMenusWithoutFocus()
        this._maybeCloseTopLevelMenu()
      }
    }

    _closeMenusWithoutFocus(){
      this.el.querySelectorAll('[ada-menu--toggler][aria-expanded="true"]').forEach(expanded => {
        const content = this._getControlledContent(expanded)
        if(expanded != document.activeElement && content && !content.contains(document.activeElement)){
          if(content.style.display == 'flex'){
            content.style.display = 'none'
            expanded.setAttribute('aria-expanded', false)
          }
        }
      })
    }

    _getMainToggler(expanded){
      return document.querySelector(`[ada-menu--nav-toggler][aria-controls="${this.el.id}"]`)
    }

    _maybeCloseTopLevelMenu(){
      const mainToggler = this._getMainToggler()
      if(mainToggler && mainToggler.offsetWidth > 0){
        this._closeMenusWithoutFocus()
        if(this.el.offsetWidth != 0){
          mainToggler.setAttribute('aria-expanded', false)
          this.el.style.display = 'none'
          this._enableScroll()

          if(document.querySelector('html').classList.contains('ada-menu-open')){
            document.querySelector('html').classList.remove('ada-menu-open')
            mainToggler.dispatchEvent(new CustomEvent('main_toggler_closed'))
          }
        }
      }
    }    

    _getControlledContent(el){
      const controlledContentId = el.getAttribute('aria-controls')
      return controlledContentId ? this.el.querySelector(`#${controlledContentId}`) : null
    }
  }

  /**
   * Start it up
   */
  function adaMenus(){
    document.querySelectorAll('[ada-menu]').forEach(menu => {
      new adaMenu(menu)
    })    
  }

  adaMenus()
}